import {useEffect} from 'react';
import {createSharedStore} from '../sharedStore';
import {
  COOKIE_CONSENT_ACCEPTED_EVENT,
  COOKIE_CONSENT_REVOKED_EVENT,
} from './constants';
// eslint-disable-next-line monorepo/no-internal-import
import {useSuspenseSafeHydrationDone} from '@aboutyou/lazy-imports/src/useSuspenseSafeHydrationDone';

export const isCookieAcceptedCookie = () => {
  // 1clss:1 is present in the cookie if the user has accepted the cookie banner
  const cookieValue = document.cookie.match(/OptanonConsent=([^;]+)/)?.[1];
  if (!cookieValue) {
    return false;
  }

  if (!cookieValue.includes('groups')) {
    return false;
  }

  if (!cookieValue.includes('1clss')) {
    return true;
  }

  const result =
    ((cookieValue?.includes('1clss:1') || cookieValue?.includes('1clss%3A1')) &&
      (cookieValue?.includes('am:1') || cookieValue?.includes('am%3A1'))) ??
    false;
  return result;
};

interface ConsentBannerState {
  isConsentBannerAccepted: boolean;
  update: (isConsentBannerAccepted: boolean) => void;
}
export const useConsentBannerStateStore = createSharedStore<ConsentBannerState>(
  set => ({
    // on the browser we need to check if the updates to any storage can be done ASAP after page load
    // otherwise we will miss storage updates that happen immediately on page load
    // Example: updating the gender cookie via MetaData endpoints
    isConsentBannerAccepted: process.browser ? isCookieAcceptedCookie() : false,
    update: (isConsentBannerAccepted: boolean) =>
      set({isConsentBannerAccepted}),
  }),
);

/**
 * Use this only if you need to know if the user has accepted the cookie banner during the render time.
 * Using this hook will cause a rerender of the component when the user accepts/rejects the cookie banner.
 * If you only need to check if the user has accepted the cookies during a callback event (eg: button click) use `getIsConsentBannerAccepted` instead since you dont need a rerender of the component in that case.
 */
export const useIsConsentBannerAccepted = () => {
  const isHydrationDone = useSuspenseSafeHydrationDone();
  const isConsentBannerAccepted = useConsentBannerStateStore(
    state => state.isConsentBannerAccepted,
  );

  if (!isHydrationDone) {
    return false;
  }

  return isConsentBannerAccepted;
};

export const getIsConsentBannerAccepted = () => {
  return useConsentBannerStateStore.getState().isConsentBannerAccepted;
};

export const useUpdateConsentBannerAcceptedState = () => {
  const updateIsConsentBannerAccepted = useConsentBannerStateStore(
    state => state.update,
  );

  useEffect(() => {
    const handler = () => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      updateIsConsentBannerAccepted(true);
    };
    window.addEventListener(COOKIE_CONSENT_ACCEPTED_EVENT, handler);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_ACCEPTED_EVENT, handler);
    };
  }, [updateIsConsentBannerAccepted]);

  useEffect(() => {
    const handler = () => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      updateIsConsentBannerAccepted(false);
    };
    window.addEventListener(COOKIE_CONSENT_REVOKED_EVENT, handler);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_REVOKED_EVENT, handler);
    };
  }, [updateIsConsentBannerAccepted]);
};
